/**
 * TipoCambioService — Tipo de Cambio oficial por firma y fecha.
 *
 * Lookup strategy:
 *   1. Query tipo_cambio table for the exact date or most recent prior date.
 *   2. If no record found, fall back to 3.750 (approximate PEN/USD) until
 *      the user registers official SBS rates via TipoCambioEditor.
 *
 * SUNAT requirement: Art. 5 Reglamento IGV DS 029-94-EF — usar TC venta SBS
 * del día de la operación para facturas en moneda extranjera.
 */
import Decimal from 'decimal.js';
import { supabase } from './supabaseClient';

export const TC_FALLBACK = 3.750;

export interface TipoCambioRow {
  id: number;
  fecha: string;         // YYYY-MM-DD
  moneda: string;
  compra: number;
  venta: number;
  fuente: 'manual' | 'sbs';
}

/**
 * Returns the TC venta for a given firm + date + currency.
 * Looks up the most recent record on or before the given date.
 */
export async function getTipoCambio(
  firmId: string,
  fecha: string,
  moneda: string = 'USD',
): Promise<number> {
  const { data } = await supabase
    .from('tipo_cambio')
    .select('venta')
    .eq('firm_id', firmId)
    .eq('moneda', moneda)
    .lte('fecha', fecha)
    .order('fecha', { ascending: false })
    .limit(1)
    .single();

  return (data as { venta: number } | null)?.venta ?? TC_FALLBACK;
}

/** Upsert a TC record (insert or update by firm+fecha+moneda). */
export async function upsertTipoCambio(
  firmId: string,
  fecha: string,
  moneda: string,
  compra: number,
  venta: number,
  fuente: 'manual' | 'sbs' = 'manual',
): Promise<void> {
  const { error } = await supabase
    .from('tipo_cambio')
    .upsert(
      { firm_id: firmId, fecha, moneda, compra, venta, fuente },
      { onConflict: 'firm_id,fecha,moneda' },
    );
  if (error) throw new Error(`TipoCambio upsert: ${error.message}`);
}

/** Load all TC records for a firm, ordered by date desc. */
export async function listTipoCambio(
  firmId: string,
  moneda: string = 'USD',
  limit = 30,
): Promise<TipoCambioRow[]> {
  const { data, error } = await supabase
    .from('tipo_cambio')
    .select('id,fecha,moneda,compra,venta,fuente')
    .eq('firm_id', firmId)
    .eq('moneda', moneda)
    .order('fecha', { ascending: false })
    .limit(limit);

  if (error) throw new Error(`TipoCambio list: ${error.message}`);
  return (data ?? []) as TipoCambioRow[];
}

/** Format a Decimal TC for SUNAT PLE output (3 decimal places). */
export function formatTc(tc: number): string {
  return new Decimal(tc).toFixed(3);
}
